/**
 * AlertThresholdsSection — defect/error/damage/stuck-bin alert levels for
 * the header-selected location (extracted from the old god component's
 * `renderAlertThresholds`).
 *
 * Per-location, so it binds to the global header LocationFilter selection
 * rather than owning its own picker. Local edits are tracked against a
 * saved-server-state ref (savedThresholdsRef); switching the header location
 * while dirty discards those edits immediately (reset to defaults) and the
 * load effect re-syncs once the new location's query resolves.
 */
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useLocation } from '@/contexts/LocationContext';
import { trpc } from '@/lib/trpc';
import { DEFAULT_THRESHOLDS, type AlertThresholds } from '@/features/production-issues/types';
import { SettingsSection } from '../components/console/SettingsSection';
import { useDirtyRegistration } from '../components/console/SettingsDirtyContext';

export function AlertThresholdsSection() {
  const { selectedLocation } = useLocation();

  const alertThresholdsQuery = trpc.productionOrders.issues.getAlertThresholds.useQuery(
    { location_id: selectedLocation?.id ?? '' },
    { enabled: !!selectedLocation?.id },
  );
  const updateAlertThresholdsMutation = trpc.productionOrders.issues.updateAlertThresholds.useMutation();

  const [alertThresholds, setAlertThresholds] = useState<AlertThresholds>(DEFAULT_THRESHOLDS);
  const [alertThresholdsLoaded, setAlertThresholdsLoaded] = useState(false);
  const savedThresholdsRef = useRef<AlertThresholds>(DEFAULT_THRESHOLDS);
  const previousLocationIdRef = useRef<string | undefined>(selectedLocation?.id);

  // Header location changed — discard any pending edits for the old
  // location and wait for the new location's query to load.
  useEffect(() => {
    if (previousLocationIdRef.current === selectedLocation?.id) return;
    previousLocationIdRef.current = selectedLocation?.id;
    setAlertThresholdsLoaded(false);
    setAlertThresholds(DEFAULT_THRESHOLDS);
    savedThresholdsRef.current = DEFAULT_THRESHOLDS;
  }, [selectedLocation?.id]);

  useEffect(() => {
    if (alertThresholdsQuery.data && !alertThresholdsLoaded) {
      const next: AlertThresholds = {
        print_defect_rate: alertThresholdsQuery.data.defect_rate_threshold,
        print_defect_window_hours: alertThresholdsQuery.data.defect_rate_window_hours,
        inventory_error_rate: alertThresholdsQuery.data.inventory_error_threshold,
        inventory_error_window_hours: 24, // Not stored in backend, use default
        pap_damage_rate: alertThresholdsQuery.data.pap_damage_threshold,
        pap_damage_window_days: 3, // Not stored in backend, use default
        stuck_bin_count: alertThresholdsQuery.data.stuck_bin_threshold,
        stuck_bin_age_hours: alertThresholdsQuery.data.stuck_bin_threshold_hours,
      };
      setAlertThresholds(next);
      savedThresholdsRef.current = next;
      setAlertThresholdsLoaded(true);
    }
  }, [alertThresholdsQuery.data, alertThresholdsLoaded]);

  const isDirty = JSON.stringify(alertThresholds) !== JSON.stringify(savedThresholdsRef.current);

  useDirtyRegistration(
    'alert-thresholds',
    isDirty && selectedLocation?.id
      ? {
          label: 'Alert thresholds',
          save: async () => {
            const locationId = selectedLocation.id;
            try {
              await updateAlertThresholdsMutation.mutateAsync({
                location_id: locationId,
                thresholds: {
                  defect_rate_threshold: alertThresholds.print_defect_rate,
                  defect_rate_window_hours: alertThresholds.print_defect_window_hours,
                  inventory_error_threshold: alertThresholds.inventory_error_rate,
                  pap_damage_threshold: alertThresholds.pap_damage_rate,
                  stuck_bin_threshold: alertThresholds.stuck_bin_count,
                  stuck_bin_threshold_hours: alertThresholds.stuck_bin_age_hours,
                },
              });
            } catch (error) {
              toast.error(
                `Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`,
              );
              throw error;
            }
            savedThresholdsRef.current = alertThresholds;
            toast.success('Alert thresholds saved');
            alertThresholdsQuery.refetch();
          },
          discard: () => setAlertThresholds(savedThresholdsRef.current),
        }
      : null,
  );

  return (
    <SettingsSection
      id="alert-thresholds"
      title="Alert thresholds"
      description="Defect, inventory-error, P&P damage and stuck-bin alert levels."
      icon={AlertTriangle}
      scope="location"
    >
      {!selectedLocation ? (
        <p className="py-4 text-center text-sm text-muted-foreground">
          Select a location in the header to configure alert thresholds.
        </p>
      ) : alertThresholdsQuery.isPending ? (
        <div className="flex items-center justify-center py-8">
          <span className="text-sm text-muted-foreground">Loading…</span>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Print Defect Rate */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Print Defect Rate</Label>
            <p className="text-xs text-muted-foreground">
              Alert when defect rate exceeds threshold in time window
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="defect-rate" className="text-xs text-muted-foreground">
                  Threshold (%)
                </Label>
                <Input
                  id="defect-rate"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={100}
                  step={0.5}
                  value={alertThresholds.print_defect_rate}
                  onChange={(e) =>
                    setAlertThresholds((prev) => ({
                      ...prev,
                      print_defect_rate: parseFloat(e.target.value) || 0,
                    }))
                  }
                  className="h-10"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="defect-window" className="text-xs text-muted-foreground">
                  Window (hours)
                </Label>
                <Input
                  id="defect-window"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={168}
                  value={alertThresholds.print_defect_window_hours}
                  onChange={(e) =>
                    setAlertThresholds((prev) => ({
                      ...prev,
                      print_defect_window_hours: parseInt(e.target.value) || 24,
                    }))
                  }
                  className="h-10"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Inventory Error Rate */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Inventory Error Rate</Label>
            <p className="text-xs text-muted-foreground">
              Alert when inventory errors exceed threshold
            </p>
            <div className="space-y-1">
              <Label htmlFor="inventory-error-rate" className="text-xs text-muted-foreground">
                Threshold (%)
              </Label>
              <Input
                id="inventory-error-rate"
                type="number"
                inputMode="decimal"
                min={0}
                max={100}
                step={0.5}
                value={alertThresholds.inventory_error_rate}
                onChange={(e) =>
                  setAlertThresholds((prev) => ({
                    ...prev,
                    inventory_error_rate: parseFloat(e.target.value) || 0,
                  }))
                }
                className="h-10 w-full sm:w-1/2"
              />
            </div>
          </div>

          <Separator />

          {/* P&P Damage Rate */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">P&P Damage Rate</Label>
            <p className="text-xs text-muted-foreground">
              Alert when pick &amp; pack damage rate exceeds threshold
            </p>
            <div className="space-y-1">
              <Label htmlFor="pap-damage-rate" className="text-xs text-muted-foreground">
                Threshold (%)
              </Label>
              <Input
                id="pap-damage-rate"
                type="number"
                inputMode="decimal"
                min={0}
                max={100}
                step={0.5}
                value={alertThresholds.pap_damage_rate}
                onChange={(e) =>
                  setAlertThresholds((prev) => ({
                    ...prev,
                    pap_damage_rate: parseFloat(e.target.value) || 0,
                  }))
                }
                className="h-10 w-full sm:w-1/2"
              />
            </div>
          </div>

          <Separator />

          {/* Stuck Bins */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Stuck Bins</Label>
            <p className="text-xs text-muted-foreground">
              Alert when bins are held longer than threshold
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="stuck-bin-count" className="text-xs text-muted-foreground">
                  Min bin count
                </Label>
                <Input
                  id="stuck-bin-count"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={1000}
                  value={alertThresholds.stuck_bin_count}
                  onChange={(e) =>
                    setAlertThresholds((prev) => ({
                      ...prev,
                      stuck_bin_count: parseInt(e.target.value) || 5,
                    }))
                  }
                  className="h-10"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="stuck-bin-hours" className="text-xs text-muted-foreground">
                  Age threshold (hours)
                </Label>
                <Input
                  id="stuck-bin-hours"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={720}
                  value={alertThresholds.stuck_bin_age_hours}
                  onChange={(e) =>
                    setAlertThresholds((prev) => ({
                      ...prev,
                      stuck_bin_age_hours: parseInt(e.target.value) || 24,
                    }))
                  }
                  className="h-10"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </SettingsSection>
  );
}
